import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  GraduationCap, BookOpen, Cloud, Wind, Thermometer, 
  Droplets, Sun, CloudRain, Snowflake, Zap, Tornado,
  ChevronRight, ChevronDown, Play, CheckCircle, Star
} from "lucide-react";
import { Button } from "./ui/button";

const LESSONS = [
  {
    id: "basics",
    title: "Weather Basics",
    icon: Cloud,
    color: "text-blue-400",
    lessons: [
      {
        id: "what-is-weather",
        title: "What is Weather?",
        duration: "3 min",
        content: `Weather is the state of the atmosphere at a particular place and time. It includes temperature, humidity, precipitation, cloudiness, visibility, and wind.

**Key factors that determine weather:**
• **Temperature** - How hot or cold the air is
• **Humidity** - The amount of water vapor in the air
• **Air Pressure** - The weight of the atmosphere pressing down
• **Wind** - Air moving from high to low pressure areas
• **Precipitation** - Water falling from clouds (rain, snow, sleet, hail)

Weather changes constantly because the atmosphere is always in motion, driven by the sun's uneven heating of Earth's surface.`
      },
      {
        id: "atmosphere",
        title: "The Atmosphere",
        duration: "4 min",
        content: `The atmosphere is a layer of gases surrounding Earth. It's divided into five main layers:

**1. Troposphere (0-12 km)**
Where weather happens! Contains 75% of atmospheric mass.

**2. Stratosphere (12-50 km)**
Contains the ozone layer. Commercial jets fly here.

**3. Mesosphere (50-80 km)**
Where meteors burn up. Coldest layer.

**4. Thermosphere (80-700 km)**
Aurora occurs here. Very hot but thin.

**5. Exosphere (700+ km)**
Gradually fades into space.

The troposphere is where all our weather occurs because it contains most of the atmosphere's water vapor.`
      },
      {
        id: "temperature",
        title: "Understanding Temperature",
        duration: "3 min",
        content: `Temperature measures the kinetic energy (motion) of air molecules. The faster they move, the warmer the air.

**Temperature Scales:**
• **Fahrenheit (°F)** - Water freezes at 32°F, boils at 212°F
• **Celsius (°C)** - Water freezes at 0°C, boils at 100°C
• **Kelvin (K)** - Scientific scale, 0K = absolute zero

**What affects temperature?**
• Latitude - Areas near the equator are warmer
• Altitude - Higher elevations are cooler
• Time of day - Warmest in afternoon, coolest at dawn
• Season - Earth's tilt causes seasonal temperature changes
• Cloud cover - Clouds trap heat at night, block sun during day`
      }
    ]
  },
  {
    id: "clouds",
    title: "Cloud Types",
    icon: Cloud,
    color: "text-white",
    lessons: [
      {
        id: "cloud-formation",
        title: "How Clouds Form",
        duration: "4 min",
        content: `Clouds form when water vapor in the air condenses into tiny water droplets or ice crystals.

**The Process:**
1. Sun heats Earth's surface
2. Warm air rises (convection)
3. As air rises, it cools
4. Cool air can't hold as much moisture
5. Water vapor condenses on tiny particles (dust, pollen, salt)
6. Billions of these droplets form a cloud

**Dew Point**
The temperature at which air becomes saturated and condensation begins. When air temperature reaches the dew point, fog or clouds form.`
      },
      {
        id: "cloud-types",
        title: "Types of Clouds",
        duration: "5 min",
        content: `Clouds are classified by altitude and shape:

**High Clouds (above 20,000 ft)**
• **Cirrus** - Wispy, feathery, made of ice crystals
• **Cirrostratus** - Thin, sheet-like, creates halos
• **Cirrocumulus** - Small, white patches (rare)

**Middle Clouds (6,500-20,000 ft)**
• **Altostratus** - Gray sheets, sun appears watery
• **Altocumulus** - White/gray patches, "mackerel sky"

**Low Clouds (below 6,500 ft)**
• **Stratus** - Flat, gray, may produce drizzle
• **Stratocumulus** - Lumpy gray/white layers
• **Nimbostratus** - Dark gray, steady rain or snow

**Vertical Development**
• **Cumulus** - Puffy "fair weather" clouds
• **Cumulonimbus** - Thunderstorm clouds, can reach 60,000 ft!`
      }
    ]
  },
  {
    id: "storms",
    title: "Severe Weather",
    icon: Zap,
    color: "text-yellow-400",
    lessons: [
      {
        id: "thunderstorms",
        title: "Thunderstorms",
        duration: "5 min",
        content: `Thunderstorms are dramatic weather events producing lightning, thunder, heavy rain, and sometimes hail and tornadoes.

**Requirements for Formation:**
1. **Moisture** - Humid air from oceans, lakes, or transpiration
2. **Lift** - Something to push air upward (fronts, mountains, heating)
3. **Instability** - Warm air below cold air

**Three Stages:**
1. **Developing** - Strong updrafts, cloud builds
2. **Mature** - Rain falls, updrafts and downdrafts coexist
3. **Dissipating** - Downdrafts dominate, rain decreases

**Lightning Safety:**
• When thunder roars, go indoors!
• If caught outside, avoid tall objects
• Stay away from water
• The 30-30 rule: If time between flash and thunder is less than 30 seconds, seek shelter for 30 minutes after last thunder`
      },
      {
        id: "tornadoes",
        title: "Tornadoes",
        duration: "5 min",
        content: `Tornadoes are violently rotating columns of air extending from thunderstorms to the ground.

**Enhanced Fujita (EF) Scale:**
• **EF0** - 65-85 mph - Light damage
• **EF1** - 86-110 mph - Moderate damage
• **EF2** - 111-135 mph - Significant damage
• **EF3** - 136-165 mph - Severe damage
• **EF4** - 166-200 mph - Devastating damage
• **EF5** - 200+ mph - Incredible destruction

**Tornado Safety:**
• Go to the lowest floor, interior room
• Stay away from windows
• Protect your head
• Mobile homes are NOT safe - evacuate to sturdy building
• If in a car, don't try to outrun - find low ground and cover head

**Warning Signs:**
• Dark, greenish sky
• Large hail
• Loud roar (like a train)
• Rotating wall cloud`
      },
      {
        id: "hurricanes",
        title: "Hurricanes",
        duration: "6 min",
        content: `Hurricanes are massive tropical cyclones with sustained winds of 74+ mph.

**Saffir-Simpson Scale:**
• **Category 1** - 74-95 mph - Minimal damage
• **Category 2** - 96-110 mph - Moderate damage  
• **Category 3** - 111-129 mph - Extensive damage (Major)
• **Category 4** - 130-156 mph - Extreme damage (Major)
• **Category 5** - 157+ mph - Catastrophic damage (Major)

**Structure:**
• **Eye** - Calm center, 20-40 miles wide
• **Eyewall** - Most intense winds and rain
• **Rainbands** - Spiral bands of thunderstorms

**Formation Requirements:**
• Warm ocean water (80°F+)
• Low wind shear
• Pre-existing disturbance
• Distance from equator (Coriolis effect)

**Names:**
Atlantic hurricanes are named from a predetermined list. Names of particularly deadly/costly storms are retired.`
      }
    ]
  },
  {
    id: "forecasting",
    title: "Weather Forecasting",
    icon: Sun,
    color: "text-orange-400",
    lessons: [
      {
        id: "how-forecasts-work",
        title: "How Forecasts Work",
        duration: "4 min",
        content: `Modern weather forecasting combines observations, physics, and supercomputers.

**Data Collection:**
• Weather stations (temperature, pressure, humidity)
• Weather balloons (upper atmosphere)
• Satellites (cloud patterns, moisture)
• Radar (precipitation)
• Aircraft reports
• Ocean buoys

**Computer Models:**
Supercomputers solve physics equations describing atmospheric motion. Major models include:
• **GFS** - American global model
• **ECMWF** - European model (often most accurate)
• **NAM** - North American regional model

**Forecast Accuracy:**
• 1-3 days: Very accurate (85-90%)
• 4-7 days: Good (75-80%)
• 8-10 days: Fair (50-65%)
• Beyond 10 days: Limited skill

The "butterfly effect" limits long-range forecasting - tiny errors amplify over time.`
      },
      {
        id: "reading-maps",
        title: "Reading Weather Maps",
        duration: "5 min",
        content: `Weather maps use symbols to show atmospheric conditions.

**Pressure Systems:**
• **H (High)** - Sinking air, clear skies, clockwise winds (N. Hemisphere)
• **L (Low)** - Rising air, clouds/storms, counterclockwise winds

**Fronts:**
• **Cold Front** ▲▲▲ (blue) - Cold air pushing warm air, storms likely
• **Warm Front** ●●● (red) - Warm air rising over cold, steady rain
• **Stationary Front** - Neither advancing, prolonged weather
• **Occluded Front** - Cold front catches warm front

**Isobars:**
Lines of equal pressure. Closely spaced = strong winds.

**Isotherms:**
Lines of equal temperature.

**Radar Colors:**
• Green - Light rain
• Yellow - Moderate rain
• Red - Heavy rain
• Purple/White - Extreme precipitation or hail`
      }
    ]
  }
];

export default function WeatherUniversity({ isDarkMode }) {
  const [expandedModule, setExpandedModule] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [completedLessons, setCompletedLessons] = useState([]);

  const toggleModule = (moduleId) => {
    setExpandedModule(expandedModule === moduleId ? null : moduleId);
  };

  const openLesson = (lesson) => {
    setSelectedLesson(lesson);
  };

  const completeLesson = (lessonId) => {
    if (!completedLessons.includes(lessonId)) {
      setCompletedLessons([...completedLessons, lessonId]);
    }
    setSelectedLesson(null);
  };

  const totalLessons = LESSONS.reduce((acc, mod) => acc + mod.lessons.length, 0);
  const progress = Math.round((completedLessons.length / totalLessons) * 100);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
            <GraduationCap className={`w-6 h-6 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
          </div>
          <div>
            <h3 className="font-bold text-lg">Weather University</h3>
            <p className="text-sm opacity-60">Learn about weather science</p>
          </div>
        </div>
        
        {/* Progress */}
        <div className="text-right">
          <p className="text-sm font-medium">{progress}% Complete</p>
          <div className="w-32 h-2 bg-white/10 rounded-full mt-1">
            <div 
              className="h-full bg-gradient-to-r from-[#00f2ea] to-[#7000ff] rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Modules */}
      <div className="space-y-3">
        {LESSONS.map((module) => {
          const IconComponent = module.icon;
          const moduleCompleted = module.lessons.every(l => completedLessons.includes(l.id));
          const moduleLessonsCompleted = module.lessons.filter(l => completedLessons.includes(l.id)).length;
          
          return (
            <div 
              key={module.id}
              className={`rounded-xl overflow-hidden ${isDarkMode ? 'bg-white/5' : 'bg-black/5'}`}
            >
              {/* Module Header */}
              <button
                onClick={() => toggleModule(module.id)}
                className="w-full flex items-center gap-3 p-4 hover:bg-white/5 transition-colors"
              >
                <IconComponent className={`w-5 h-5 ${module.color}`} />
                <div className="flex-1 text-left">
                  <h4 className="font-semibold">{module.title}</h4>
                  <p className="text-xs opacity-50">
                    {moduleLessonsCompleted}/{module.lessons.length} lessons completed
                  </p>
                </div>
                {moduleCompleted && (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                )}
                <ChevronDown 
                  className={`w-5 h-5 opacity-50 transition-transform ${
                    expandedModule === module.id ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {/* Lessons */}
              <AnimatePresence>
                {expandedModule === module.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t border-white/10"
                  >
                    {module.lessons.map((lesson) => {
                      const isCompleted = completedLessons.includes(lesson.id);
                      
                      return (
                        <button
                          key={lesson.id}
                          onClick={() => openLesson(lesson)}
                          className={`w-full flex items-center gap-3 p-3 pl-12 hover:bg-white/5 transition-colors ${
                            isCompleted ? 'opacity-60' : ''
                          }`}
                        >
                          {isCompleted ? (
                            <CheckCircle className="w-4 h-4 text-green-400" />
                          ) : (
                            <Play className="w-4 h-4 opacity-50" />
                          )}
                          <span className="flex-1 text-left text-sm">{lesson.title}</span>
                          <span className="text-xs opacity-40">{lesson.duration}</span>
                          <ChevronRight className="w-4 h-4 opacity-40" />
                        </button>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {/* Lesson Modal */}
      <AnimatePresence>
        {selectedLesson && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={() => setSelectedLesson(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className={`w-full max-w-2xl max-h-[80vh] overflow-hidden rounded-2xl ${
                isDarkMode ? 'bg-[#0a0a0a]' : 'bg-white'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Lesson Header */}
              <div className={`p-4 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <BookOpen className="w-5 h-5 text-[#00f2ea]" />
                    <h3 className="font-bold">{selectedLesson.title}</h3>
                  </div>
                  <span className="text-sm opacity-50">{selectedLesson.duration}</span>
                </div>
              </div>

              {/* Lesson Content */}
              <div className="p-6 overflow-y-auto max-h-[50vh]">
                <div className="prose prose-invert max-w-none">
                  {selectedLesson.content.split('\n\n').map((paragraph, idx) => (
                    <p key={`para-${idx}-${paragraph.slice(0,20)}`} className="mb-4 text-sm leading-relaxed opacity-80 whitespace-pre-line">
                      {paragraph.split('**').map((part, i) => 
                        i % 2 === 0 ? part : <strong key={`bold-${i}`} className="text-[#00f2ea]">{part}</strong>
                      )}
                    </p>
                  ))}
                </div>
              </div>

              {/* Lesson Footer */}
              <div className={`p-4 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                <div className="flex justify-between">
                  <Button variant="ghost" onClick={() => setSelectedLesson(null)}>
                    Close
                  </Button>
                  <Button 
                    onClick={() => completeLesson(selectedLesson.id)}
                    className="bg-gradient-to-r from-[#00f2ea] to-[#7000ff] text-white"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {completedLessons.includes(selectedLesson.id) ? 'Completed' : 'Mark Complete'}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
